import formatters from './formatters';
import { formatNumberDefault } from './formatters/format-number-default';
import { CATALOG_UNIT_KEYS } from './shared/catalog-constants';
import { unitRegistry } from './shared/instance';
import { resolveDisplayTarget } from './shared/resolve-display-target';
import {
  resolveCompositeDisplayFormatterKey,
  resolveFormatTransformTarget,
} from './shared/resolve-format-transform-target';
import { FormattedUnitLabels, FormatUnitsInput, NormalizedUnitValue } from './shared/types';
import { toNumber, toRawValue, unitValueToFormattedUnitLabels } from './shared/utils';
import { transformUnits } from './transform-units';

const { fallbackFormatters } = unitRegistry;

const formatUnregistered = (transformed: NormalizedUnitValue): FormattedUnitLabels => {
  if (typeof transformed.value === 'boolean') {
    return fallbackFormatters.boolean({ value: transformed.value });
  }

  // Only fully-numeric values round here; see UX-4313.
  const raw = transformed.value;
  const numeric = toNumber(raw);
  const isFullyNumeric =
    typeof raw === 'number' ||
    (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw)));

  if (numeric != null && isFullyNumeric) {
    return formatNumberDefault({ value: numeric, unitLabel: transformed.units });
  }

  return unitValueToFormattedUnitLabels({ value: raw, units: transformed.units });
};

/**
 * Formats a unit value to a formatted unit labels object. Transforms exactly once,
 * then formats exactly once — formatters can no longer transform (FormatterInput
 * has no target-unit parameter for them to transform toward). Numeric precision
 * formatters are wrapped at registry build time to coerce and null-guard input.
 * Bespoke formatters handle their own value types.
 *
 * When displayUnits names a presentation (duration, direction, boolean family, etc.),
 * that formatter always applies. Otherwise the formatter is chosen from the value's
 * post-transform units — the target when conversion succeeded, the source when it did not.
 */
export const formatUnits = (
  input: FormatUnitsInput,
  displayUnits?: string
): FormattedUnitLabels => {
  const source: NormalizedUnitValue = {
    value: toRawValue(input.value),
    units: input.units === '' || input.units == null ? CATALOG_UNIT_KEYS.NONE : input.units,
  };

  // 1. Resolve what to convert toward (transformTarget) vs what owns formatting
  //    (presentationKey — duration, direction, boolean families, etc.).
  const { presentationKey, transformTarget } = resolveDisplayTarget(displayUnits);

  // 2. Convert once. resolveFormatTransformTarget may override transformTarget for
  //    display-only rules (e.g. vacuum → inHg, or skip transform for psig-inhg display).
  const transformed = transformUnits(source, resolveFormatTransformTarget(source, transformTarget));

  // 3. Pick the formatter. Usually this is the post-transform unit (psi → °F formatter).
  //    psig-inhg display is special: formatPsigInhg is sign-to-label only (negative → inHG
  //    magnitude, non-negative → PSIG) and is not a real conversion target.
  const resolvedUnits = transformed.units.toLowerCase();
  // resolveCompositeDisplayFormatterKey applies composite display only when
  // compositeDisplayAppliesTo(source, transformTarget) is true.
  const formatterKey =
    presentationKey ??
    resolveCompositeDisplayFormatterKey(source.units, transformTarget) ??
    (resolvedUnits && resolvedUnits !== CATALOG_UNIT_KEYS.NONE
      ? resolvedUnits
      : (transformTarget?.toLowerCase() ?? resolvedUnits));
  const formatter = formatters[formatterKey];

  return formatter ? formatter({ value: transformed.value }) : formatUnregistered(transformed);
};
